import { ProductType } from 'constants/productType';
import { useEffect, useState, useCallback } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { AccountType, AmountRange, convertEnumToMultiSelectionAnswer, InvestmentRange } from 'views/utils/enums';
import {
  AnswerCompatibilityParam,
  useAnswerCompatibilityMutation,
  useGetAccountTypeQuery,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';

interface IQuestion<T> {
  key: string;
  kind: string;
  answer?: (answerValue: T) => CompatibilityCheckState;
}

export interface IYesOrNoQuestion extends IQuestion<boolean> {
  kind: 'yesOrNo' | 'yesOrNoAndFile';
  questionText: string;
  helpText?: string;
  file?: string;
}

export type MultiSelectionAnswer = {
  value: number;
  label: string;
};

export interface IMultiSelectionQuestion extends IQuestion<MultiSelectionAnswer> {
  kind: 'multiSelection';
  questionText: string;
  selections: Array<MultiSelectionAnswer>;
}
export type SelectAndTextAnswer = {
  values: Array<{
    value: number;
    label: string;
  }>;
  text: string;
};
export interface ISelectAndTextQuestion extends IQuestion<SelectAndTextAnswer> {
  kind: 'selectAndText' | 'selectOnly';
  questionText: string;
  selections: Array<{
    value: number;
    label: string;
  }>;
}

export interface IGroupYesOrNoQuestion extends IQuestion<Array<boolean>> {
  kind: 'groupYesOrNo';
  questionTexts: Array<string>;
}

export type CompatibilityCheckQuestion =
  | IYesOrNoQuestion
  | ISelectAndTextQuestion
  | IGroupYesOrNoQuestion
  | IMultiSelectionQuestion;
export type CompatibilityCheckAnswer = boolean | SelectAndTextAnswer | Array<boolean> | string | MultiSelectionAnswer;

// 回答開始
interface ICheckStateStart {
  kind: 'start';
}
const checkStateStart: ICheckStateStart = {
  kind: 'start',
};

// 回答結果の状態
// 次の質問を続ける
interface ICheckStateContinue {
  kind: 'continue';
  question: CompatibilityCheckQuestion;
  /**
   * 回答結果 戻った場合に設定する
   */
  answered?: CompatibilityCheckAnswer;
}

// 回答確認
interface ICheckStateConfirm {
  kind: 'confirm';
}
export const checkStateConfirm: ICheckStateConfirm = {
  kind: 'confirm',
};

// 回答完了
interface ICheckStateComplete {
  kind: 'complete';
}
const checkStateComplete: ICheckStateComplete = {
  kind: 'complete',
};

// 取引不可
interface ICheckStateImpossible {
  kind: 'impossible';
}
const checkStateImpossible: ICheckStateImpossible = {
  kind: 'impossible',
};

export type CompatibilityCheckState =
  | ICheckStateStart
  | ICheckStateContinue
  | ICheckStateConfirm
  | ICheckStateComplete
  | ICheckStateImpossible;

const LeaseFundProps = () => {
  const question1: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `日本型オペレーティング・リースの仕組みについて
      
      ・日本型オペレーティング・リース事業は、投資家が匿名組合出資、任意組合出資、信託受益権取得等の方法により、航空機、船舶、海上輸送コンテナ等のリース事業に投資するものです。
      ・多くの場合、リース物件の購入に際して事業者は購入代金の一部を金融機関等からの借入れで調達します。お客様には、リース事業の結果生じた損益及び収支が出資持分の割合に応じて分配されます。お客様は、分配されるリース事業の損益を本業の決算と合算することで、本業によって得られる利益を将来に繰り延べる投資効果を得ることができます。
      ・日本型オペレーティング・リースは、出資元本・利回りが保証された商品ではなく、利子・配当等を主たる目的とした商品でもありません。また、投資にあたってのリスクはお客様自身の責任において負っていただきます。
      
      以上の日本型オペレーティング・リース事業の仕組みの概略について貴社は、具体的に理解されていますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question2,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question2: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `当社の取り扱う日本型オペレーティング・リース事業は外貨建てのものがあり、為替リスクがあります。為替レートの変動により、償還時において、案件組成時の為替レートから円高に進んでいた場合で円貨に換算すると、出資元本が毀損する可能性があります。貴社は、この為替リスクの内容について、具体的に理解され、受け入れることができますか？`,

    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question3,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question3: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `リース取引の当事者である貸借人、転借人、保証人や当社グループの債務不履行や倒産のため、予定されたリース料収入が得られなかったり、リース物件を市場で売却せざるを得なかった場合に出資元本の全部または一部が毀損する可能性があります。貴社は、この信用リスクの内容について、具体的に理解され、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question4,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question4: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `リース契約の中途解約が発生した場合や物件の貸借人が購入選択権を行使しない場合等には、対象物件を売却するか新たな貸借人を探したりすることで投資資金の回収を図ることとなります。リース料やリース物件の市場価格変動や費用発生により、出資元本の全部または一部が毀損する可能性があります。貴社は、この残存価格等リスクの内容について、具体的に理解され、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question5,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question5: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `事業者がリース物件取得のため金融機関等から借入を行ってレバレッジをかけている場合、借入金の返済が優先される結果、わずかな売却額の下落でも出資金回収率が大きく低下します。貴社は、このレバレッジ効果について、具体的に理解され、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question6,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question6: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q6',
    questionText: `日本型オペレーティング・リース事業は、これまでご確認いただいた事項のほか、以下のようなリスクや留意点があります。
      ・税制・法制・会計制度等の変更により、当初予定した投資効果が得られない可能性があります。
      ・リース物件が事故等により使用不能または修復不可能な損害を被った場合、リース契約が中途解約になることにより、出資元本が当初の予定より早く、若しくは遅れて返還される可能性があります。
      ・リース事業の遂行のために合理的な理由をもって必要と判断された場合には、現金による追加出資をご負担いただく可能性があります。
      ・本商品への出資は、クーリング・オフの対象外であり、また預金保険機構や投資者保護基金の対象ではありません。
      ・当社の出資日からお客様の出資日までの経過利息相当額、組成手数料などの諸費用をご負担いただきます。
      ・個々の商品におけるリスクや留意事項は、それぞれの商品の「契約締結前交付書面」に記載しておりますので、ご出資に際しては、当該書面をよくご覧いただき、納得のうえでご出資いただく必要があります。
      ・貴社は、これらのリスクや留意点の内容を具体的に理解され、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question7,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question7: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q7',
    questionText: `貴社は、日本型オペレーティング・リース商品は中途解約できないこと、また、出資元本は保証されていないことを具体的に理解され、受け入れることができますか？また投資資金は、投資期間中に他に使用する必要がない余裕資金であり、出資元本の毀損があったとしても本業に影響ないものでなければならないことを具体的に理解され、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question8,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question8: ISelectAndTextQuestion = {
    kind: 'selectAndText',
    key: 'q8',
    questionText: `貴社にお聞きします。
      本件投資の目的として、もっとも重視されるものは何ですか？（複数選択可）`,
    selections: [
      {
        label: '財務戦略',
        value: 1,
      },
      {
        label: '資金運用',
        value: 2,
      },
      {
        label: 'その他',
        value: 3,
      },
    ],
    answer: (_answerValue: {
      values: Array<{
        value: number;
        label: string;
      }>;
      text: string;
    }) => {
      return checkStateConfirm;
    },
  };

  return {
    question1,
    question2,
    question3,
    question4,
    question5,
    question6,
    question7,
    question8,
  };
};

const PersonalDomesticRealEstateProps = () => {
  const question1: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `本信託受益権の仕組みについて

本信託受益権は、信託の受託者が委託者の指図に基づき、受託した不動産の管理・処分を行い、その成果を受益者に給付する仕組みです。信託財産（不動産及び金銭等）を管理・処分した成果を受益者にお受け取りいただく実績配当の商品ですので、信託の受託者又はその他の者が一定の配当額及び信託終了時の元本価額（信託財産の価額）をお約束するものではありません。そのため、投資にあたってのリスクはお客様自身の責任において負っていただきます。
以上の本信託受益権の仕組みの概略について、お客様の投資に関する知識やご経験を踏まえて、十分に理解いただけましたか？「はい」の場合には、以下のリスクに関してお答えください。`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question2,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question2: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `価格変動リスクについて
本信託受益権の主な信託財産は不動産であるため、一般の経済指標（景気動向、金利水準、為替相場等）のほか、主に不動産市況（不動産価格、賃貸市場、賃料水準など）の動向には大きな影響を受けやすい商品であり、お客様に確定した配当や元本交付をお約束するものではありませんし、元本や収益が減少してお客様に損失が生じるおそれがあります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,

    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question3,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question3: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `信用リスクについて
本信託受益権の主な信託財産は不動産であるため、本信託受益権の受託者、委託者の信用状況の変化等の他に、信託不動産の賃借人（賃借人からの転借人及びさらに数次にわたり転々貸等が行われる場合は全ての転々借人等を含む。）等、管理委託先、建物の設計･施工会社等、本信託受益権に関係する者の信用状況の変化等を直接又は間接の原因として、元本や収益が減少してお客様に損失が生じるおそれがあり、お客様に確定した配当や元本交付をお約束するものではありません。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question4,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question4: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `税制変更リスクについて
不動産に適用される税制（所得税、固定資産税、都市計画税、相続税、贈与税等）の変更により、当初予定した投資効果を得られないことがあります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question5,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question5: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `流動性リスクについて
本信託受益権は株式等の金融商品と比べて流動性が低いため、お客様の希望する時期や条件で処分できない場合があります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question6,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question6: IMultiSelectionQuestion = {
    kind: 'multiSelection',
    key: 'q6',
    questionText: `お客様の保有する金融資産の状況についてご教示ください。`,
    selections: convertEnumToMultiSelectionAnswer(InvestmentRange),
    answer: (_answerValue) => {
      return {
        kind: 'continue',
        question: question7,
      };
    },
  };
  const question7: IMultiSelectionQuestion = {
    kind: 'multiSelection',
    key: 'q7',
    questionText: `お客様のご年収についてご教示ください。`,
    selections: convertEnumToMultiSelectionAnswer(AmountRange),
    answer: (_answerValue) => {
      return {
        kind: 'continue',
        question: question8,
      };
    },
  };
  const question8: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q8',
    questionText: `本信託受益権への投資金額は、お客様の保有する金融資産やご年収に対して無理のない範囲である必要があります。投資元本は、投資期間中に他に使用する必要がない余裕資金ですか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question9,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question9: ISelectAndTextQuestion = {
    kind: 'selectOnly',
    key: 'q9',
    questionText: `お客様にお聞きします。
本件投資の目的として、もっとも重視されるものは何ですか？（複数選択可）`,
    selections: [
      {
        label: '資産運用',
        value: 1,
      },
      {
        label: '資産承継',
        value: 2,
      },
    ],
    answer: (_answerValue: {
      values: Array<{
        value: number;
        label: string;
      }>;
      text: string;
    }) => {
      return checkStateConfirm;
    },
  };

  return {
    question1,
    question2,
    question3,
    question4,
    question5,
    question6,
    question7,
    question8,
    question9,
  };
};

const CorporateDomesticRealEstateProps = () => {
  const question1: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `本信託受益権の仕組みについて

本信託受益権は、信託の受託者が委託者の指図に基づき、受託した不動産の管理・処分を行い、その成果を受益者に給付する仕組みです。信託財産（不動産及び金銭等）を管理・処分した成果を受益者にお受け取りいただく実績配当の商品ですので、信託の受託者又はその他の者が一定の配当額及び信託終了時の元本価額（信託財産の価額）をお約束するものではありません。そのため、投資にあたってのリスクはお客様自身の責任において負っていただきます。
以上の本信託受益権の仕組みの概略について、お客様の投資に関する知識やご経験を踏まえて、十分に理解いただけましたか？「はい」の場合には、以下のリスクに関してお答えください。`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question2,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question2: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `価格変動リスクについて
本信託受益権の主な信託財産は不動産であるため、一般の経済指標（景気動向、金利水準、為替相場等）のほか、主に不動産市況（不動産価格、賃貸市場、賃料水準など）の動向には大きな影響を受けやすい商品であり、お客様に確定した配当や元本交付をお約束するものではありませんし、元本や収益が減少してお客様に損失が生じるおそれがあります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question3,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question3: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `信用リスクについて
本信託受益権の主な信託財産は不動産であるため、本信託受益権の受託者、委託者の信用状況の変化等の他に、信託不動産の賃借人（賃借人からの転借人及びさらに数次にわたり転々貸等が行われる場合は全ての転々借人等を含む。）等、管理委託先、建物の設計･施工会社等、本信託受益権に関係する者の信用状況の変化等を直接又は間接の原因として、元本や収益が減少してお客様に損失が生じるおそれがあり、お客様に確定した配当や元本交付をお約束するものではありません。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question4,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question4: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `税制変更リスクについて
不動産に適用される税制（法人税、固定資産税、都市計画税、相続税、贈与税等）の変更により、当初予定した投資効果を得られないことがあります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question5,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question5: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `流動性リスクについて
本信託受益権は株式等の金融商品と比べて流動性が低いため、お客様の希望する時期や条件で処分できない場合があります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question6,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question6: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q6',
    questionText: `本信託受益権への投資金額は、お客様の財務状況、事業収益およびその予測に対して無理のない範囲である必要があります。投資元本は、投資期間中に他に使用する必要がない余裕資金ですか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question7,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question7: ISelectAndTextQuestion = {
    kind: 'selectOnly',
    key: 'q7',
    questionText: `お客様にお聞きします。
本件投資の目的として、もっとも重視されるものは何ですか？（複数選択可）`,
    selections: [
      {
        label: '財務戦略',
        value: 1,
      },
      {
        label: '資金運用',
        value: 2,
      },
    ],
    answer: (_answerValue: {
      values: Array<{
        value: number;
        label: string;
      }>;
      text: string;
    }) => {
      return checkStateConfirm;
    },
  };

  return {
    question1,
    question2,
    question3,
    question4,
    question5,
    question6,
    question7,
  };
};

const OverseasRealEstateProps = () => {
  const question1: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `●本組合が行う事業の仕組みについて
本件投資は、株式会社ＦＰＧ（以下、ＦＰＧ）を業務執行組合員とする民法に基づき設立した任意組合への出資です。本組合は、海外不動産を保有し賃貸事業等を行う海外リミテッドパートナーシップ１（以下、ＬＰＳ１）の出資持分を保有する海外リミテッドパートナーシップ２（以下、ＬＰＳ２）に対して、他の任意組合と共に出資を行うものになります。
本組合の業務執行組合員であるＦＰＧおよびＬＰＳ２のゼネラルパートナーは、ＦＰＧの完全子会社である株式会社ＦＰＧ証券（以下、ＦＰＧ証券）と投資一任契約を締結し、それぞれＬＰＳ２出資持分およびＬＰＳ１出資持分の管理処分を行う権限の全部をＦＰＧ証券に委託します。ＬＰＳ１は、ＬＰＳ２からの出資金および金融機関からのノンリコースローンによって対象物件を取得し、賃貸事業から得るインカムゲインおよび対象物件の再販市場での売却等によるキャピタルゲインの獲得を追求します。
本組合への出資は、上記のような仕組みをもって行われる海外不動産への投資となりますが、利回りは確定しておらず、また、物件の稼動率、賃料水準および物件の売却価格等も保証されておりません。そのため、投資にあたってのリスクはお客様自身の責任において負っていただきます。
以上の本組合の仕組みの概略について、お客様の投資に関する知識やご経験を踏まえて、十分に理解いただけましたか？
「はい」の場合には、次ページ以降のリスクに関してお答えください。`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question2,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question2: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `●物件価値変動リスク・賃貸事業環境悪化リスク・市場リスクについて
本件投資においては、対象物件の稼動状況（空室率）、賃料水準（周辺相場によるものおよび対象物件固有の原因によるものを含む）、金利、賃貸事業の必要経費の変動により、組合収益の減少または元本の毀損が生じることがあります。また、対象物件を再販市場で売却する場合、当初想定した価格で売却できず本組合へのご出資の元本が毀損する可能性があります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question3,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question3: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `●スキーム関係者の債務不履行および倒産等リスクについて
・業務執行組合員であるＦＰＧが債務不履行や倒産等の事態に陥り、本組合の運営を継続することが不可能となる場合、本組合の解散を余儀なくされ、その清算手続における対象物件の売却価格によっては、本組合へのご出資の元本が毀損する可能性があります。
・また、不動産管理会社が債務不履行や倒産等の事態に陥った場合、対象物件の運用や保険の設定更新等の管理に支障が出る可能性があります。これにより、新たな不動産管理会社を選任した場合、毎月の管理手数料の額が変更となる可能性があります。その結果、事業収支および損益が当初予定のものから変動します。
これらのリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question4,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question4: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `●流動性リスクについて
お客様が、お客様の事情により組合契約を中途解約することはできません。組合契約上の権利、義務または地位の一部または全部を譲渡したり、担保に供したりすることも原則できません。倒産等に起因して本組合から脱退する場合、ご出資の元本が毀損する可能性および当初出資の元本を超過する損失が発生する可能性があります。
また一般的に、不動産は、代替性が低く、個別性が強いため、国債・社債・株式等の金融商品に比べ流動性が低いと言えます。加えて、経済環境や不動産需給関係の影響等により、対象物件を希望時期・希望条件で処分できない場合があります。
このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question5,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question5: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `●借入に関するリスク、追加出資リスクについて
業務執行組合員が本事業の遂行のために合理的な理由をもって必要と判断した場合には、投資家に対して持分割合に応じて現金による追加出資をご負担いただく可能性があります。スキームによっては契約締結前交付書面に記載のローン期間終了時にローンの借換えを行う場合がありますが、借換えに係るローン金額がローン期間終了時の返済金額に満たない場合において、その差額について本組合が支払うべき金額が組合財産を上回るときには、追加出資をご負担いただく可能性があります。また、対象物件の売却条件に、対象物件の修繕、追加の整備、仕様変更等が課せられたり、対象物件の維持費用、保険料、融資の延⾧費用等が発生する可能性があり、本組合が支出すべき費用がその時点における組合財産を上回る場合は、追加出資をご負担いただく可能性があります。
このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question6,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question6: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q6',
    questionText: `●為替リスクについて
投資期間中に現金分配を行う場合は外貨建てになります。また、借入を含む海外不動産事業にかかる取引等は外貨建てで行われ、対象物件を売却する場合、海外不動産の売却代金は外貨建てになります。これらを円貨に換算する場合に為替変動の影響を受け、事業収支および損益が当初予定のものから変動します。そのため、為替の状況によっては、本組合へのご出資の元本が毀損する可能性があります。このリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question7,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question7: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q7',
    questionText: `●その他 各種リスクについて
本件投資については、これまでご確認いただいた事項のほか、以下のようなリスクや留意点があります。
・対象物件の全部または一部が火災、天災その他事故等により使用不能または修復不可能な損害（経済的全損）を被った場合、賃貸事業ができなくなり、収入が得られないこととなり、また、保険で補填されない場合には対象物件の原状回復費用が発生し、事業収支および損益が当初予定のものから変動します。
・対象物件の事故等により第三者に損害が発生した場合、ＬＰＳ１は対象物件の所有者としてかかる損害に関して賠償責任を負う可能性があります。ＬＰＳ１が賠償責任を負う場合、ＬＰＳ２を通じ本組合の組合財産からＬＰＳ１が賠償により被る損害を補償する義務を負うことになります。しかし、本組合の組合財産により損害を補償できず、さらに保険会社が損害賠償のための保険金を支払えない場合や賠償額が受取保険金額を上回る場合、本組合の組合員に費用負担が発生する結果、投資家に追加出資をご負担いただく可能性があります。
・税制・法制・会計制度等の変更により、新たな税負担が発生し、また当初予定した投資効果を得られない可能性または当初予定した権利義務が実現されない可能性があります。さらには、本事業の継続を含めて、予期せぬ影響を及ぼす可能性もあります。これらの場合には、当事者が問題解決に向けての協議を行うことになります。問題が解決できない場合には、対象物件を売却する場合があります。これらいずれの場合も、本組合へのご出資の元本が毀損する可能性、売却等に伴う費用の予期せぬ発生等によりお客様に追加出資をご負担いただく可能性、または、投資期間が延⾧される可能性があります。
・業務執行組合員は、唯一の業務執行組合員として、本組合、本事業および組合財産等に関し組合員を代理する権限を授与されており、業務執行組合員が組合契約上の関連条項記載の権限を専属的に有し、本組合の存続中、かかる選任および権限の授与を取消すことはできません。お客様は、一般組合員として、業務執行組合員および本組合を代表または代理する権限を有さず、本事業の遂行または組合財産の取得、保有、運用、管理、処分に関して一切の権限を有しません。
・本組合においては各組合員が組合の債権者に対して無限責任を負うため、お客様は組合が負担する債務につき出資額を超えて責任を負担する可能性があります。
・ＬＰＳ１は金融機関からの融資を活用して対象物件を購入しております。この融資契約には借り手側に履行義務や財務制限などを課す特約事項が盛り込まれており、予期せぬ費用の発生や想定外のタイミングでの対象物件の売却などを強いられる可能性があります。また、融資を活用することで「レバレッジ効果」が生じるため、事業損益が増加する局面では出資額に対する利益率を押し上げることとなりますが、事業損益が損失となる局面では出資額に対する損失率を拡大させることとなります。
・個々の商品におけるリスクや留意事項は、それぞれの商品の「契約締結前交付書面」に記載しておりますので、ご出資に際しては、当該書面をよくご覧いただき、納得のうえでご出資いただく必要があります。
これらのリスクの内容について、お客様の投資に関する知識やご経験を踏まえて、十分に理解し、受け入れることができますか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question8,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question8: IYesOrNoQuestion = {
    kind: 'yesOrNo',
    key: 'q8',
    questionText: `●本組合への出資金額は、お客様の財務状況、事業収益およびその予測に対して無理のない範囲である必要があります。出資元本は、投資期間中に他に使用する必要がない余裕資金ですか？`,
    answer: (yesOrNo: boolean) => {
      if (yesOrNo) {
        return {
          kind: 'continue',
          question: question9,
        };
      } else {
        return checkStateImpossible;
      }
    },
  };
  const question9: ISelectAndTextQuestion = {
    kind: 'selectOnly',
    key: 'q9',
    questionText: `●お客様にお聞きします。
本件投資の目的として、もっとも重視されるものは何ですか？（複数選択可）`,
    selections: [
      {
        label: '財務戦略',
        value: 1,
      },
      {
        label: '資金運用',
        value: 2,
      },
    ],
    answer: (_answerValue: {
      values: Array<{
        value: number;
        label: string;
      }>;
      text: string;
    }) => {
      return checkStateConfirm;
    },
  };

  return {
    question1,
    question2,
    question3,
    question4,
    question5,
    question6,
    question7,
    question8,
    question9,
  };
};

export const leaseFundWebQuestionList: CompatibilityCheckQuestion[] = Object.values(LeaseFundProps());
export const personalDomesticRealEstateWebQuestionList: CompatibilityCheckQuestion[] = Object.values(
  PersonalDomesticRealEstateProps(),
);
export const corporateDomesticRealEstateWebQuestionList: CompatibilityCheckQuestion[] = Object.values(
  CorporateDomesticRealEstateProps(),
);
export const overseasRealEstateWebQuestionList: CompatibilityCheckQuestion[] = Object.values(OverseasRealEstateProps());

export type CompatibilityCheckQuestionToAnswer = {
  question: CompatibilityCheckQuestion;
  answer: CompatibilityCheckAnswer;
};

export const qaListToAnswerCompatibilityParam = (
  assetType: ProductType,
  qaList: Array<CompatibilityCheckQuestionToAnswer>,
  success: boolean,
  accountType?: AccountType,
): AnswerCompatibilityParam => {
  switch (assetType) {
    case ProductType.LeaseFund: {
      const { question1, question2, question3, question4, question5, question6, question7, question8 } =
        LeaseFundProps();
      const answer8 = qaList.find((x) => x.question.key === question8.key)?.answer as SelectAndTextAnswer;
      return {
        LeaseFund: {
          Question1: qaList.find((x) => x.question.key === question1.key)?.answer as boolean,
          Question2: qaList.find((x) => x.question.key === question2.key)?.answer as boolean | undefined,
          Question3: qaList.find((x) => x.question.key === question3.key)?.answer as boolean | undefined,
          Question4: qaList.find((x) => x.question.key === question4.key)?.answer as boolean | undefined,
          Question5: qaList.find((x) => x.question.key === question5.key)?.answer as boolean | undefined,
          Question6: qaList.find((x) => x.question.key === question6.key)?.answer as boolean | undefined,
          Question7: qaList.find((x) => x.question.key === question7.key)?.answer as boolean | undefined,
          Question8: answer8?.values?.map((x) => x.value),
          Question8OtherText: answer8?.text,
          Success: success,
        },
      };
    }
    case ProductType.DomesticRealEstate: {
      if (accountType == AccountType.Corporate) {
        const { question1, question2, question3, question4, question5, question6, question7 } =
          CorporateDomesticRealEstateProps();
        const answer7 = qaList.find((x) => x.question.key === question7.key)?.answer as SelectAndTextAnswer;

        return {
          CorporateDomesticRealEstate: {
            Question1: qaList.find((x) => x.question.key === question1.key)?.answer as boolean,
            Question2: qaList.find((x) => x.question.key === question2.key)?.answer as boolean | undefined,
            Question3: qaList.find((x) => x.question.key === question3.key)?.answer as boolean | undefined,
            Question4: qaList.find((x) => x.question.key === question4.key)?.answer as boolean | undefined,
            Question5: qaList.find((x) => x.question.key === question5.key)?.answer as boolean | undefined,
            Question6: qaList.find((x) => x.question.key === question6.key)?.answer as boolean | undefined,
            Question7: answer7?.values?.map((x) => x.value),
            Success: success,
          },
        };
      } else {
        const { question1, question2, question3, question4, question5, question6, question7, question8, question9 } =
          PersonalDomesticRealEstateProps();
        const answer9 = qaList.find((x) => x.question.key === question9.key)?.answer as SelectAndTextAnswer;

        return {
          PersonalDomesticRealEstate: {
            Question1: qaList.find((x) => x.question.key === question1.key)?.answer as boolean,
            Question2: qaList.find((x) => x.question.key === question2.key)?.answer as boolean | undefined,
            Question3: qaList.find((x) => x.question.key === question3.key)?.answer as boolean | undefined,
            Question4: qaList.find((x) => x.question.key === question4.key)?.answer as boolean | undefined,
            Question5: qaList.find((x) => x.question.key === question5.key)?.answer as boolean | undefined,
            Question6: (qaList.find((x) => x.question.key === question6.key)?.answer as MultiSelectionAnswer)?.value as
              | number
              | undefined,
            Question7: (qaList.find((x) => x.question.key === question7.key)?.answer as MultiSelectionAnswer)?.value as
              | number
              | undefined,
            Question8: qaList.find((x) => x.question.key === question8.key)?.answer as boolean | undefined,
            Question9: answer9?.values?.map((x) => x.value),
            Success: success,
          },
        };
      }
    }
    case ProductType.OverseasRealEstate: {
      const { question1, question2, question3, question4, question5, question6, question7, question8, question9 } =
        OverseasRealEstateProps();
      const answer9 = qaList.find((x) => x.question.key === question9.key)?.answer as SelectAndTextAnswer;

      return {
        OverseasRealEstate: {
          Question1: qaList.find((x) => x.question.key === question1.key)?.answer as boolean,
          Question2: qaList.find((x) => x.question.key === question2.key)?.answer as boolean | undefined,
          Question3: qaList.find((x) => x.question.key === question3.key)?.answer as boolean | undefined,
          Question4: qaList.find((x) => x.question.key === question4.key)?.answer as boolean | undefined,
          Question5: qaList.find((x) => x.question.key === question5.key)?.answer as boolean | undefined,
          Question6: qaList.find((x) => x.question.key === question6.key)?.answer as boolean | undefined,
          Question7: qaList.find((x) => x.question.key === question7.key)?.answer as boolean | undefined,
          Question8: qaList.find((x) => x.question.key === question8.key)?.answer as boolean | undefined,
          Question9: answer9?.values?.map((x) => x.value),
          Success: success,
        },
      };
    }
  }
};

export const useCompatibilityCheck = (assetType: ProductType) => {
  const { showError } = useGlobalToastHandler();
  const [checkState, setCheckState] = useState<CompatibilityCheckState>(checkStateStart);
  const [checkStateForImpossible, setCheckStateForImpossible] = useState<CompatibilityCheckState>();
  const [questionToAnswers, setAnswers] = useState<Array<CompatibilityCheckQuestionToAnswer>>([]);
  const [_answerCompatibilityResponse, answerCompatibility] = useAnswerCompatibilityMutation();
  const [accountInfo, _] = useGetAccountTypeQuery();
  const start = useCallback(() => {
    setAnswers([]);
    setCheckState({
      kind: 'continue',
      question: (() => {
        switch (assetType) {
          case ProductType.LeaseFund:
            return LeaseFundProps().question1;
          case ProductType.DomesticRealEstate:
            if (accountInfo.data?.GetAccountInfo?.AccountType!! == AccountType.Corporate) {
              return CorporateDomesticRealEstateProps().question1;
            } else {
              return PersonalDomesticRealEstateProps().question1;
            }
          case ProductType.OverseasRealEstate:
            return OverseasRealEstateProps().question1;
        }
      })(),
    });
  }, [accountInfo.data?.GetAccountInfo?.AccountType]);

  const next = useCallback(
    (answerValue: CompatibilityCheckAnswer) => {
      if (checkState.kind === 'continue') {
        const question = checkState.question;
        let res: CompatibilityCheckState;
        switch (question.kind) {
          case 'yesOrNo':
          case 'yesOrNoAndFile':
            res = question.answer!(answerValue as boolean);
            break;
          case 'selectAndText':
          case 'selectOnly':
            res = question.answer!(answerValue as SelectAndTextAnswer);
            break;
          case 'groupYesOrNo':
            res = question.answer!(answerValue as Array<boolean>);
            break;
          case 'multiSelection':
            res = question.answer!(answerValue as MultiSelectionAnswer);
            break;
        }

        setAnswers((state) => [
          ...state,
          {
            question,
            answer: answerValue,
          },
        ]);

        if (res.kind === 'impossible') {
          // ページ遷移する前にエラーを表示する必要がある(エラーが起きていた場合)ので、専用のステータスを変えて、その状態を監視してハンドリングする
          setCheckStateForImpossible(res);
        } else {
          setCheckState(res);
        }
      }
    },
    [checkState],
  );

  const backQuestion = useCallback(() => {
    if (checkState.kind === 'continue' || checkState.kind === 'confirm') {
      if (questionToAnswers.length === 0) {
        setCheckState(checkStateStart);
      } else {
        // 最後の回答を取り除きcheckStateにansweredとして保持する 取り除いておくことでnextでは気にせずに追加するだけで良くなる
        // 1つ前の回答結果は保持するが2つ前に戻った場合は次へ進んでも値は消えている状態になる
        const lastQa = questionToAnswers[questionToAnswers.length - 1];
        setAnswers((answers) => answers.slice(0, -1));
        setCheckState({
          kind: 'continue',
          question: lastQa.question,
          // 最後の回答を初期値として各画面で表示する
          answered: lastQa.answer,
        });
      }
    }
  }, [checkState, questionToAnswers]);

  const impossible = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await answerCompatibility({
          input: qaListToAnswerCompatibilityParam(
            assetType,
            questionToAnswers,
            false,
            accountInfo.data?.GetAccountInfo?.AccountType!,
          ),
        });

        if (!res?.data) return;

        if (res.data.AnswerCompatibility?.Code !== 0) {
          showError(res.data.AnswerCompatibility?.Message);
          setCheckStateForImpossible(undefined);
          // 処理失敗の場合、一つ戻してあげないと失敗状態が残ったままになるのでおかしくなる
          backQuestion();
        } else {
          setCheckState({
            kind: 'impossible',
          });
        }
      },
    },
    [questionToAnswers, accountInfo.data?.GetAccountInfo?.AccountType!],
  );

  useEffect(() => {
    if (checkStateForImpossible?.kind === 'impossible') {
      impossible();
    }
  }, [checkStateForImpossible]);

  const complete = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await answerCompatibility({
          input: qaListToAnswerCompatibilityParam(
            assetType,
            questionToAnswers,
            true,
            accountInfo.data?.GetAccountInfo?.AccountType!,
          ),
        });
        if (!res?.data) return;

        if (res.data.AnswerCompatibility?.Code === 0) {
          setCheckState(checkStateComplete);
        } else {
          showError(res.data.AnswerCompatibility?.Message);
        }
      },
    },
    [checkState, questionToAnswers, accountInfo.data?.GetAccountInfo?.AccountType!],
  );

  return {
    checkState,
    questionToAnswers,
    setAnswers,
    start,
    next,
    backQuestion,
    complete,
  };
};
